/**
 * AdminDashboard Component
 *
 * Main admin console dashboard showing:
 * - Aggregate platform metrics (customers, jobs, sourcing, outreach)
 * - Conversion funnel metrics
 * - Health indicators (reply rate, open rate, bounce rate)
 * - Navigation to other admin views (customers, jobs, audit trail)
 * - Active admin impersonation session status/controls, if any
 *
 * Uses service-role queries to bypass RLS for total visibility.
 * All data includes tenant_id for context and audit purposes.
 *
 * ------------------------------------------------------------------
 * Migrated for "Implement RTK Query hooks and caching for all API
 * endpoints": previously fetched via a hand-rolled `useEffect` +
 * local `useState`, and rendered hard-coded mock `AdminDashboardMetrics`/
 * `AdminFunnelMetrics` behind a `// TODO: Call GET /api/admin/dashboard`
 * comment. Now:
 * - Data fetching: `useAdminDashboard()` (`@/lib/api/apiClient`) — a
 *   TanStack Query hook, Zod-validated response, shared cache/retry
 *   policy (same pattern as `app/routes/admin/Subscriptions.tsx`).
 * - Loading state: ShadCN `Skeleton` instead of a plain text string.
 * - Error state: `sonner` `toast.error` + inline retry.
 * - Impersonation: `useApiStore`'s `impersonationSession` (Zustand) +
 *   `useEndImpersonation()` (TanStack mutation) feed `<ImpersonationPanel>`
 *   so ending a session updates client state and clears the active
 *   session banner without a page reload.
 *
 * Related: Issue #284, docs/v2/08-admin-platform-ai-agent.md
 *
 * ------------------------------------------------------------------
 * UPDATE (GitHub issue #499, "E2E: Admin dashboard — outreach metrics,
 * company filter, job table"):
 * - "Platform Metrics" (4 cards) + "Outreach Health" (3 cards) below are
 *   this dashboard's 7 `data-testid="summary-card"` summary cards (the
 *   ticket's "Dashboard Metrics" test-case group — "7 summary cards
 *   render with correct labels" / "Cards show metric values from API").
 * - Added the "Backfill Sourcing" / "Refresh Replies" buttons (ticket's
 *   "Actions" test-case group) — `useBackfillSourcing()` /
 *   `useRefreshReplies()` (`@/lib/api/apiClient`), toasting on success/
 *   error the same way `useEndImpersonation` does.
 * - Added `useAdminReplyNotifications()` (ticket's "Real-time Updates"
 *   test-case group) — see that hook's module doc-comment
 *   (`@/lib/realtime/adminReplyNotifications`) for the WebSocket wire
 *   contract and the backend gap it's waiting on.
 * - NOT done here (see `app/routes/admin/JobsList.tsx`'s module
 *   doc-comment for the corresponding TODO): the ticket's "Company
 *   Filter" test-case group and the "Sourcing Jobs Table" test-case
 *   group's full column set (candidates/queued/sent/followups/opened/
 *   bounced/replied/shortlisted/declined) — neither a `company` field
 *   nor those per-status outreach counters exist on `AdminJobInfo`
 *   (`@/lib/validation/admin`) or its backing `sourcing_jobs`/`jobs`
 *   tables (`db/schema.ts`), and per `CLAUDE.md`'s "Schema authority"
 *   rule those aren't something an E2E-test-focused issue should
 *   hand-add without a matching `supabase/migrations/*.sql` migration.
 *   `e2e/admin-dashboard.spec.ts` marks those specific cases
 *   `test.fixme(...)` rather than faking a passing assertion.
 *
 * ------------------------------------------------------------------
 * UPDATE (GitHub issue #563, "[Metrics] Admin dashboard Opened/Bounced
 * cards are GLOBAL — ignore the customer filter"):
 * - Added the customer-scope dropdown (`data-testid=
 *   "admin-dashboard-customer-filter"`) — populated from
 *   `useAdminCustomers()` (the same real customers list
 *   `CustomersList.tsx` already uses), writing the selection to
 *   `useApiStore`'s `adminDashboardCustomerId`. Selecting a customer
 *   threads their `uuid` into `useAdminDashboard(customerId)` as a
 *   `user_id` query param, so every card driven by that query
 *   automatically refetches scoped to them — the actual fix: before this
 *   issue, `useAdminDashboard()` took no arguments at all, so its query
 *   key never varied with any customer selection.
 * - Added the "Candidates Sent" / "Opened" / "Bounced" cards (a new
 *   "Customer Outreach" section) sourced from the new
 *   `total_candidates_sent`/`total_opened`/`total_bounced` fields on
 *   `AdminDashboardMetrics` (`apps/workers/src/api/admin/dashboard.ts`'s
 *   real, customer-scoped `outreach_messages` query — see that file's
 *   own doc-comment for the fix). The "Opened" card additionally clamps
 *   its displayed value to never exceed "Candidates Sent" for the same
 *   render (`displayedOpened` below) — belt-and-suspenders on top of the
 *   backend's own structural guarantee
 *   (`computeCustomerOutreachMetrics`), so the UI itself can never render
 *   "Opened > Sent" even given a stale/inconsistent API response.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  useAdminDashboard,
  useAdminCustomers,
  useEndImpersonation,
  useBackfillSourcing,
  useRefreshReplies,
  reportApiError,
} from '@/lib/api/apiClient';
import { useAdminReplyNotifications } from '@/lib/realtime/adminReplyNotifications';
import { useApiStore } from '@/stores/apiStore';
import { Skeleton } from '@/components/ui/skeleton';
import { ImpersonationPanel } from '@/components/admin/ImpersonationPanel';

/** Default (unfiltered) params for the customer-dropdown's own
 * `useAdminCustomers()` fetch — a large `limit` since this dropdown wants
 * every customer, not a paginated page of them. */
const DASHBOARD_CUSTOMER_OPTIONS_PARAMS = {
  limit: 200,
  offset: 0,
  search: '',
  plan: '' as const,
  status: '' as const,
};

export default function AdminDashboard() {
  const navigate = useNavigate();

  // GitHub issue #563: customer-scope dropdown selection — `''` means
  // "platform-wide, unscoped".
  const selectedCustomerId = useApiStore((state) => state.adminDashboardCustomerId);
  const setSelectedCustomerId = useApiStore((state) => state.setAdminDashboardCustomerId);

  const { data, isLoading, isError, error, refetch, isFetching } =
    useAdminDashboard(selectedCustomerId);
  const customersList = useAdminCustomers(DASHBOARD_CUSTOMER_OPTIONS_PARAMS);

  const impersonationSession = useApiStore((state) => state.impersonationSession);
  const endImpersonation = useEndImpersonation();
  const backfillSourcing = useBackfillSourcing();
  const refreshReplies = useRefreshReplies();

  // Ticket's "Real-time Updates" test-case group — subscribes for the
  // lifetime of this component, toasts on a `new_reply` WebSocket
  // message. No-op (renders nothing, throws nothing) if runtime config
  // isn't loaded yet or the WebSocket connection fails.
  useAdminReplyNotifications();

  // Requirement #6/#8: sonner error toast, fired once per failed fetch
  // (not once per re-render) — same pattern as
  // `app/routes/admin/Subscriptions.tsx`.
  useEffect(() => {
    if (isError) {
      reportApiError(error, 'Failed to load dashboard metrics');
    }
  }, [isError, error]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 space-y-4" aria-busy="true">
          <Skeleton className="h-8 w-48" />
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
          <Skeleton className="h-40 w-full" />
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center max-w-md">
          <div className="text-lg font-semibold text-destructive">Error Loading Dashboard</div>
          <p className="text-sm text-muted-foreground mt-2">
            {error instanceof Error ? error.message : 'Failed to fetch dashboard metrics'}
          </p>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="mt-4 px-4 py-2 border border-border rounded-md hover:bg-accent text-sm font-medium disabled:opacity-50"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const metrics = data.metrics;
  const funnel = data.funnel;
  const customerOptions = customersList.data?.data ?? [];

  // GitHub issue #563 fallback logic: "Opened" can never exceed
  // "Candidates Sent" for the same scope (the bug's own smoking gun: a
  // customer with Candidates Sent = 0 must never render Opened > 0). The
  // backend already guarantees this structurally
  // (`computeCustomerOutreachMetrics`), but this clamp is a second,
  // independent line of defense in the UI itself against a stale or
  // otherwise-inconsistent API response.
  const displayedOpened = Math.min(metrics.total_opened, metrics.total_candidates_sent);

  // GitHub issue "[Dashboard] Real customer funnel breakdown (not '603 /
  // 60 / 0')" — always exactly 10 rows, in pipeline order (see
  // `candidate_funnel_breakdown`'s doc-comment, `@herohire/types`).
  const candidateFunnelBreakdown = data.candidate_funnel_breakdown;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold">Admin Console</h1>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/admin/customers')}
              className="px-3 py-2 border border-border rounded-md hover:bg-accent text-sm font-medium"
            >
              Customers
            </button>
            <button
              onClick={() => navigate('/admin/jobs')}
              className="px-3 py-2 border border-border rounded-md hover:bg-accent text-sm font-medium"
            >
              Jobs
            </button>
            <button
              onClick={() => navigate('/admin/audit')}
              className="px-3 py-2 border border-border rounded-md hover:bg-accent text-sm font-medium"
            >
              Audit Trail
            </button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Active impersonation session banner (Requirement #2: mutation
            success -> Zustand store update -> UI reflects the new state) */}
        {impersonationSession && (
          <ImpersonationPanel
            impersonatedUserId={impersonationSession.impersonatedUserId}
            expiresAt={impersonationSession.expiresAt}
            sessionId={impersonationSession.sessionId}
            isLoading={endImpersonation.isPending}
            error={endImpersonation.error ? endImpersonation.error.message : undefined}
            onEndSession={async () => {
              await endImpersonation.mutateAsync({ sessionId: impersonationSession.sessionId });
            }}
          />
        )}

        {/* Customer-scope dropdown — GitHub issue #563's fix: every
            top-line card below must respect this selection. `''` (the
            default "All Customers" option) means platform-wide/unscoped;
            selecting a customer threads their `uuid` into
            `useAdminDashboard()` as a `user_id` query param. */}
        <section className="mb-8">
          <label htmlFor="admin-dashboard-customer-filter" className="block text-sm font-medium mb-1">
            Customer
          </label>
          <select
            id="admin-dashboard-customer-filter"
            data-testid="admin-dashboard-customer-filter"
            value={selectedCustomerId}
            onChange={(e) => setSelectedCustomerId(e.target.value)}
            className="px-3 py-2 border border-border rounded-md text-sm w-full md:w-80"
          >
            <option value="">All Customers</option>
            {customerOptions.map((customer) => (
              <option key={customer.uuid} value={customer.uuid}>
                {customer.name ?? customer.email}
                {customer.company_name ? ` - ${customer.company_name}` : ''}
              </option>
            ))}
          </select>
        </section>

        {/* Key Metrics Grid — GitHub issue #499's "Dashboard Metrics" test
            group ("7 summary cards render with correct labels" / "Cards
            show metric values from API") covers this section plus
            "Outreach Health" below: 4 + 3 = 7 `data-testid="summary-card"`
            cards total. GitHub issue #563 adds a further 3 cards in the
            "Customer Outreach" section below (Candidates Sent/Opened/
            Bounced), all scoped by the customer dropdown above. */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Platform Metrics</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {/* Customers */}
            <div className="p-4 border border-border rounded-lg" data-testid="summary-card">
              <p className="text-sm text-muted-foreground mb-1">Active Customers</p>
              <p className="text-2xl font-bold">{metrics.active_customers}</p>
              <p className="text-xs text-muted-foreground mt-2">{metrics.total_customers} total</p>
            </div>

            {/* Jobs */}
            <div className="p-4 border border-border rounded-lg" data-testid="summary-card">
              <p className="text-sm text-muted-foreground mb-1">Active Jobs</p>
              <p className="text-2xl font-bold">{metrics.active_jobs}</p>
              <p className="text-xs text-muted-foreground mt-2">{metrics.total_jobs} total</p>
            </div>

            {/* Sourcing Today */}
            <div className="p-4 border border-border rounded-lg" data-testid="summary-card">
              <p className="text-sm text-muted-foreground mb-1">Sourcing Runs (Today)</p>
              <p className="text-2xl font-bold">
                {metrics.successful_sourcing_runs_today} / {metrics.total_sourcing_runs_today}
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                {metrics.total_sourcing_runs_today > 0
                  ? (
                      (metrics.successful_sourcing_runs_today / metrics.total_sourcing_runs_today) *
                      100
                    ).toFixed(1)
                  : 0}
                % success rate
              </p>
            </div>

            {/* Outreach Today */}
            <div className="p-4 border border-border rounded-lg" data-testid="summary-card">
              <p className="text-sm text-muted-foreground mb-1">Outreach (Today)</p>
              <p className="text-2xl font-bold">{metrics.total_outreach_sent_today}</p>
              <p className="text-xs text-muted-foreground mt-2">
                {metrics.total_replies_received_today} replies
              </p>
            </div>
          </div>
        </section>

        {/* Health Metrics */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Outreach Health</h2>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="p-4 border border-border rounded-lg" data-testid="summary-card">
              <p className="text-sm text-muted-foreground mb-1">Average Open Rate</p>
              <p className="text-2xl font-bold">{(metrics.avg_open_rate * 100).toFixed(1)}%</p>
            </div>

            <div className="p-4 border border-border rounded-lg" data-testid="summary-card">
              <p className="text-sm text-muted-foreground mb-1">Average Reply Rate</p>
              <p className="text-2xl font-bold">{(metrics.avg_reply_rate * 100).toFixed(1)}%</p>
            </div>

            <div className="p-4 border border-border rounded-lg" data-testid="summary-card">
              <p className="text-sm text-muted-foreground mb-1">Average Bounce Rate</p>
              <p className="text-2xl font-bold">{(metrics.avg_bounce_rate * 100).toFixed(1)}%</p>
            </div>
          </div>
        </section>

        {/* Customer Outreach — GitHub issue #563's fix. These 3 cards
            (Candidates Sent/Opened/Bounced) are the ones the issue
            reports as GLOBAL/ignoring the customer dropdown; all three
            are now derived from the same customer-scoped
            `outreach_messages` query (`total_candidates_sent`/
            `total_opened`/`total_bounced` on `AdminDashboardMetrics`) and
            refetch whenever the dropdown above changes. */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Customer Outreach</h2>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="p-4 border border-border rounded-lg" data-testid="summary-card">
              <p className="text-sm text-muted-foreground mb-1">Candidates Sent</p>
              <p className="text-2xl font-bold">{metrics.total_candidates_sent}</p>
            </div>

            <div className="p-4 border border-border rounded-lg" data-testid="summary-card">
              <p className="text-sm text-muted-foreground mb-1">Opened</p>
              <p className="text-2xl font-bold">{displayedOpened}</p>
            </div>

            <div className="p-4 border border-border rounded-lg" data-testid="summary-card">
              <p className="text-sm text-muted-foreground mb-1">Bounced</p>
              <p className="text-2xl font-bold">{metrics.total_bounced}</p>
            </div>
          </div>
        </section>

        {/* Candidate Funnel Breakdown — GitHub issue "[Dashboard] Real
            customer funnel breakdown (not '603 / 60 / 0')". Replaces the
            old opaque "603 candidates / 60 queued / 0 sent" text with a
            real, per-stage breakdown (sourced -> enriched -> reachable ->
            verified -> top-N -> queued -> sent -> opened -> replied ->
            shortlisted), with the drop between every adjacent pair of
            stages shown. Sourced from `data.candidate_funnel_breakdown`
            (`apps/workers/src/api/admin/dashboard.ts`), scoped by the SAME
            customer dropdown as every other card on this page. */}
        <section className="mb-8" data-testid="candidate-funnel-breakdown">
          <h2 className="text-xl font-semibold mb-1">Candidate Funnel Breakdown</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Real per-stage candidate counts, with the drop shown between each stage.
          </p>
          <div className="grid gap-3 grid-cols-2 md:grid-cols-5">
            {candidateFunnelBreakdown.map((row) => (
              <div
                key={row.stage}
                className="p-3 border border-border rounded-lg"
                data-testid={`funnel-stage-${row.stage}`}
                data-stage-available={row.available}
              >
                <p className="text-xs font-medium text-muted-foreground mb-1">{row.label}</p>
                {row.available ? (
                  <p className="text-xl font-bold" data-testid={`funnel-stage-${row.stage}-count`}>
                    {row.count}
                  </p>
                ) : (
                  <p
                    className="text-sm font-semibold text-destructive"
                    data-testid={`funnel-stage-${row.stage}-unavailable`}
                  >
                    Unavailable
                  </p>
                )}
                {row.available && row.drop !== null && (
                  <p
                    className="text-xs text-muted-foreground mt-1"
                    data-testid={`funnel-stage-${row.stage}-drop`}
                  >
                    {row.drop > 0 ? `-${row.drop} from previous stage` : 'No drop from previous stage'}
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Sourcing/Outreach Actions — ticket's "Actions" test-case group */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Sourcing &amp; Outreach Actions</h2>
          <div className="flex flex-wrap gap-3">
            <button
              data-testid="backfill-sourcing-button"
              onClick={() =>
                backfillSourcing.mutate(undefined, {
                  onSuccess: (result) => {
                    toast.success(
                      `Sourcing backfill triggered — ${result.jobsQueued} job(s) queued`
                    );
                  },
                })
              }
              disabled={backfillSourcing.isPending}
              className="px-4 py-2 border border-border rounded-md hover:bg-accent text-sm font-medium disabled:opacity-50"
            >
              {backfillSourcing.isPending ? 'Backfilling…' : 'Backfill Sourcing'}
            </button>

            <button
              data-testid="refresh-replies-button"
              onClick={() =>
                refreshReplies.mutate(undefined, {
                  onSuccess: (result) => {
                    toast.success(`Replies refreshed — ${result.syncedCount} synced`);
                  },
                })
              }
              disabled={refreshReplies.isPending}
              className="px-4 py-2 border border-border rounded-md hover:bg-accent text-sm font-medium disabled:opacity-50"
            >
              {refreshReplies.isPending ? 'Refreshing…' : 'Refresh Replies'}
            </button>
          </div>
        </section>

        {/* Conversion Funnel */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Conversion Funnel</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
            <div className="p-4 border border-border rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Leads</p>
              <p className="text-2xl font-bold">{funnel.leads}</p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Signups</p>
              <p className="text-2xl font-bold">{funnel.signups}</p>
              <p className="text-xs text-muted-foreground mt-2">
                {(funnel.conversion_lead_to_signup * 100).toFixed(1)}% from leads
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Activated</p>
              <p className="text-2xl font-bold">{funnel.activated}</p>
              <p className="text-xs text-muted-foreground mt-2">
                {(funnel.conversion_signup_to_activated * 100).toFixed(1)}% from signups
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Paid</p>
              <p className="text-2xl font-bold">{funnel.paid}</p>
              <p className="text-xs text-muted-foreground mt-2">
                {(funnel.conversion_activated_to_paid * 100).toFixed(1)}% conversion
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Retained</p>
              <p className="text-2xl font-bold">{funnel.retained}</p>
              <p className="text-xs text-muted-foreground mt-2">
                {(funnel.retention_rate * 100).toFixed(1)}% retention
              </p>
            </div>
          </div>
        </section>

        {/* Business Metrics */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Business Metrics</h2>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="p-4 border border-border rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Total MRR</p>
              <p className="text-2xl font-bold">
                $
                {(metrics.mrr_total / 100).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">MRR Growth</p>
              <p className="text-2xl font-bold">{(metrics.mrr_growth_rate * 100).toFixed(1)}%</p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Churn Rate</p>
              <p className="text-2xl font-bold">{(metrics.churn_rate * 100).toFixed(1)}%</p>
            </div>
          </div>
        </section>

        {/* Quick Actions */}
        <section>
          <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
          <div className="grid gap-4 md:grid-cols-3">
            <button
              onClick={() => navigate('/admin/customers')}
              className="p-4 border border-border rounded-lg hover:bg-accent text-left transition-colors"
            >
              <p className="font-semibold">Browse Customers</p>
              <p className="text-sm text-muted-foreground mt-1">
                View, search, and filter all tenants and users
              </p>
            </button>

            <button
              onClick={() => navigate('/admin/jobs')}
              className="p-4 border border-border rounded-lg hover:bg-accent text-left transition-colors"
            >
              <p className="font-semibold">Browse Jobs</p>
              <p className="text-sm text-muted-foreground mt-1">
                Drill into job details, candidates, and outreach
              </p>
            </button>

            <button
              onClick={() => navigate('/admin/audit')}
              className="p-4 border border-border rounded-lg hover:bg-accent text-left transition-colors"
            >
              <p className="font-semibold">Audit Trail</p>
              <p className="text-sm text-muted-foreground mt-1">
                View async jobs, webhooks, and admin actions
              </p>
            </button>

            {/* GitHub issue "[Admin] Per-customer health dashboard —
                Stripe billing × platform activity
                (jobs->sourced->sent->replies)" — this issue's Entry
                Point: "/admin (via Admin panel -> Customer Health)". */}
            <button
              onClick={() => navigate('/admin/customer-health')}
              className="p-4 border border-border rounded-lg hover:bg-accent text-left transition-colors"
              data-testid="admin-quick-action-customer-health"
            >
              <p className="font-semibold">Customer Health</p>
              <p className="text-sm text-muted-foreground mt-1">
                Stripe billing × activity per paying customer — who's
                actually getting value
              </p>
            </button>

            {/* GitHub issue "[Email] Move cold outreach off the brand
                domain — dedicated, warmed, rotated sending domains"
                (#188/#516). */}
            <button
              onClick={() => navigate('/admin/sending-domains')}
              className="p-4 border border-border rounded-lg hover:bg-accent text-left transition-colors"
              data-testid="admin-quick-action-sending-domains"
            >
              <p className="font-semibold">Sending Domains</p>
              <p className="text-sm text-muted-foreground mt-1">
                Active cold-outreach rotation + per-domain SPF/DKIM/DMARC
                status
              </p>
            </button>
          </div>
        </section>
      </main>
    </div>
  );
}
